/**
 * Path traversal (TRD-06 §2).
 *
 * An enemy's position is ONE scalar: `pathT`, the distance it has travelled in tiles
 * (R-06-02). `x`/`y` are derived from it every tick and are never integrated directly
 * (R-06-03). That distinction is the whole reason a daily run produces the same score on
 * two machines: integrating a velocity accumulates float error over a 40-tile path across
 * 25 waves, and two runs that started identical end up several tiles apart. A scalar that
 * is only ever added to, then resolved by lookup, cannot drift that way.
 */
import type { PathSpec, Point } from '../scenario.js';

export interface CompiledPath {
  id: number;
  /** Cumulative distance at each waypoint; `cumulative[0]` is 0. */
  readonly cumulative: readonly number[];
  readonly waypoints: readonly Point[];
  readonly totalLength: number;
}

/**
 * Precomputed once at run start (R-06-01) — never per enemy, never per tick.
 *
 * Waypoints are authored as TILE coordinates (integers = tile corners); compilation
 * shifts them half a tile so enemies walk tile CENTERS — the same convention placed
 * towers use (`tileX + 0.5`). Before this, enemies hugged the top-left edge of the
 * drawn path and cut corners visibly on turns.
 */
export function compilePath(path: PathSpec): CompiledPath {
  const waypoints: Point[] = path.waypoints.map((w) => ({ x: w.x + 0.5, y: w.y + 0.5 }));
  const cumulative: number[] = [0];
  let total = 0;

  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1] as Point;
    const b = waypoints[i] as Point;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    total += Math.sqrt(dx * dx + dy * dy);
    cumulative.push(total);
  }

  return { id: path.id, cumulative, waypoints, totalLength: total };
}

export function compilePaths(paths: readonly PathSpec[]): CompiledPath[] {
  return paths.map(compilePath);
}

/**
 * Resolve `pathT` to a world position, writing into `out` to avoid allocating per enemy
 * per tick.
 *
 * Binary search over at most 40 segments (R-06-25). Caching the segment index on the
 * enemy was considered and rejected there: invalidating the cache when speed changes,
 * when a chill lands, or when a splitter inherits a parent's `pathT` costs more than the
 * five comparisons it saves.
 */
export function resolvePosition(path: CompiledPath, pathT: number, out: Point): void {
  const { cumulative, waypoints } = path;

  if (pathT <= 0) {
    const first = waypoints[0] as Point;
    out.x = first.x;
    out.y = first.y;
    return;
  }
  if (pathT >= path.totalLength) {
    const last = waypoints[waypoints.length - 1] as Point;
    out.x = last.x;
    out.y = last.y;
    return;
  }

  // Largest index whose cumulative distance is <= pathT.
  let lo = 0;
  let hi = cumulative.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((cumulative[mid] as number) <= pathT) lo = mid;
    else hi = mid - 1;
  }

  const segmentStart = cumulative[lo] as number;
  const segmentEnd = cumulative[lo + 1] as number;
  const a = waypoints[lo] as Point;
  const b = waypoints[lo + 1] as Point;

  const span = segmentEnd - segmentStart;
  const t = span > 0 ? (pathT - segmentStart) / span : 0;
  out.x = a.x + (b.x - a.x) * t;
  out.y = a.y + (b.y - a.y) * t;
}

/** Pick the path a group spawns on; groups without one round-robin by index (R-06-11). */
export function pathForGroup(
  paths: readonly CompiledPath[],
  declared: number | undefined,
  groupIndex: number,
): CompiledPath | undefined {
  if (paths.length === 0) return undefined;
  if (declared !== undefined) {
    return paths.find((p) => p.id === declared) ?? paths[groupIndex % paths.length];
  }
  return paths[groupIndex % paths.length];
}
